// One editor_capture_frames frame, with the browser work injected so the
// decision logic is testable: the preview render (layout, subtitles, texts,
// effects) first, the raw source frame as a fallback, then shrink and upload.
// Never throws: every failure becomes a per-frame error.

export const RAW_FRAME_NOTE = 'Raw video frame: the preview could not be rendered, so subtitles, '
  + 'texts, layout and effects are NOT shown. Do not re-apply edits because they are missing here.';

/** Per-frame budget for the preview render (it cannot finish in a hidden tab
 *  and its own backstop is 60 s, too long for a turn that waits 3 min). */
export const PREVIEW_RENDER_BUDGET_MS = 20000;

/** Per-frame budget for the raw source frame. A hidden tab does not load the
 *  video, so without it the frame never arrives and the whole batch of editor
 *  results is never sent: the turn stays stuck until it expires. */
export const SOURCE_FRAME_BUDGET_MS = 15000;

export type CapturedFrame =
  | { time: number; url: string; source: 'preview' }
  | { time: number; url: string; source: 'video'; note: string }
  | { time: number; error: string };

export interface CaptureFrameDeps {
  renderPreview: (frame: number, signal: AbortSignal) => Promise<string | null>;
  extractSource: (sourceTime: number) => Promise<string | null>;
  shrink: (dataUrl: string) => Promise<string>;
  upload: (dataUrl: string, sourceTime: number) => Promise<string | null>;
  isPageHidden: () => boolean;
  previewBudgetMs: number;
  sourceBudgetMs: number;
}

// `work` or null once `ms` pass (the timer is always cleared).
async function within<T>(work: Promise<T | null>, ms: number, onTimeout?: () => void): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const budget = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      onTimeout?.();
      resolve(null);
    }, ms);
  });
  try {
    return await Promise.race([work.catch(() => null), budget]);
  } finally {
    clearTimeout(timer);
  }
}

async function renderWithin(deps: CaptureFrameDeps, frame: number): Promise<string | null> {
  if (deps.isPageHidden()) return null;
  const controller = new AbortController();
  return within(
    deps.renderPreview(frame, controller.signal),
    deps.previewBudgetMs,
    () => controller.abort(),
  );
}

export async function captureFrameAt(
  { time, sourceTime, frame }: { time: number; sourceTime: number; frame: number },
  deps: CaptureFrameDeps,
): Promise<CapturedFrame> {
  let dataUrl = await renderWithin(deps, frame);
  const source: 'preview' | 'video' = dataUrl ? 'preview' : 'video';
  if (!dataUrl) dataUrl = await within(deps.extractSource(sourceTime), deps.sourceBudgetMs);
  if (!dataUrl) return { time, error: 'capture_failed' };
  const full = dataUrl;
  const small = await deps.shrink(full).catch(() => full);
  const url = await deps.upload(small, sourceTime).catch(() => null);
  if (!url) return { time, error: 'upload_failed' };
  return source === 'preview'
    ? { time, url, source }
    : { time, url, source, note: RAW_FRAME_NOTE };
}
